/**
 * The recap's look, from the concept video. Colours are CSS variables in
 * `wrapped.css`; this maps them to where they are used: one colour block per
 * card, a colour per person, the share image's themes, and the fonts.
 */

import type { Act } from "./types";

export const FONT_DISPLAY = "var(--wr-font-display)";
export const FONT_BODY = "var(--wr-font-ui)";
export const FONT_MONO = "var(--wr-font-mono)";

/** A colour from `wrapped.css`, e.g. `wr("yellow")` is `var(--wr-yellow)`. */
export const wr = (name: string) => `var(--wr-${name})`;

export const INK = wr("ink");
export const PAPER = wr("white");
export const PAGE = wr("page");

export const ACT_LABELS: Record<Act, string> = {
  you: "You",
  chat: "The chat",
  friends: "Friends",
  share: "Share",
};

export type CardLook = {
  bg: string;
  fg: string;
  /** Where the card's colour grows out from as it opens, as fractions of the stage. */
  from: [number, number];
};

const look = (bg: string, fg: "ink" | "white", from: [number, number]): CardLook => ({ bg: wr(bg), fg: wr(fg), from });

/** Each card's colour block, keyed by the deck's card id. */
export const CARD_LOOKS: Record<string, CardLook> = {
  intro: look("ink", "white", [0.5, 0.5]),
  "not-around": look("ink", "white", [0.5, 0.5]),
  messages: look("blurple", "white", [0.5, 1]),
  "top-people": look("fuchsia", "ink", [1, 0]),
  words: look("green", "ink", [0, 0.5]),
  emoji: look("yellow", "ink", [0.5, 0.5]),
  sites: look("deep", "white", [0, 0]),
  clock: look("sky", "ink", [0.5, 0]),
  "biggest-day": look("orange", "ink", [1, 1]),
  streak: look("navy", "white", [0, 1]),
  "then-vs-now": look("lavender", "ink", [1, 0.5]),
  calls: look("teal", "ink", [0.5, 1]),
  awards: look("purple", "white", [0.5, 0.5]),
  personality: look("cyan", "ink", [1, 0.5]),
  "chat-numbers": look("ink", "white", [0.5, 0.5]),
  names: look("yellow", "ink", [0.5, 1]),
  "chat-calls": look("orange", "ink", [0, 0]),
  "word-of-month": look("green", "ink", [1, 1]),
  joins: look("fuchsia", "ink", [0, 0.5]),
  gif: look("blurple", "white", [1, 0.5]),
  duo: look("fuchsia", "ink", [0, 0]),
  "chat-clock": look("navy", "white", [1, 0]),
  "chat-gif": look("purple", "white", [0.5, 1]),
  polls: look("cyan", "ink", [0, 1]),
  "quiz-1": look("blurple", "white", [0, 1]),
  "quiz-2": look("teal", "ink", [1, 0]),
  "quiz-3": look("yellow", "ink", [0, 0]),
  ceremony: look("ink", "white", [0.5, 0]),
  personalities: look("lavender", "ink", [1, 1]),
  share: look("fuchsia", "white", [0.5, 1]),
};

export const lookFor = (id: string): CardLook => CARD_LOOKS[id] ?? look("ink", "white", [0.5, 0.5]);

/** People get colours in roster order, so everyone keeps one colour on every card. */
export const PERSON_COLOURS = ["blurple", "fuchsia", "green", "yellow", "cyan", "orange", "purple", "teal"].map(wr);

export type ThemeId = "ink" | "sun" | "mint" | "sky" | "paper";
export const THEME_IDS: ThemeId[] = ["ink", "sun", "mint", "sky", "paper"];

/** A share theme's colours as CSS (for the page). */
export const shareTheme = (id: ThemeId) => ({
  bg: wr(`theme-${id}-bg`),
  fg: wr(`theme-${id}-fg`),
  accent: wr(`theme-${id}-accent`),
  accent2: wr(`theme-${id}-accent2`),
});

/** A share theme's colours as values (for the canvas, which can't read `var()`). */
export function shareThemeValues(id: ThemeId) {
  const styles = getComputedStyle(document.documentElement);
  const read = (part: string) => styles.getPropertyValue(`--wr-theme-${id}-${part}`).trim();
  return { bg: read("bg"), fg: read("fg"), accent: read("accent"), accent2: read("accent2") };
}

/** A token's value, for drawing on a canvas. */
export function tokenValue(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(`--wr-${name}`).trim();
}
